import { useEffect, useRef, useState, type ReactNode } from 'react';
import { HashRouter, NavLink, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AssistantHost } from './components/AssistantHost';
import { MobileTableCards } from './components/MobileTableCards';
import { scrollStep } from './utils/scrollDirection';
import { GroupWorkContext } from './components/GroupWorkContext';
import type { AuthMe, Device } from '@cornerstone/shared';
import { Playlists } from './pages/Playlists';
import { Schedules } from './pages/Schedules';
import { DeviceDetail } from './pages/DeviceDetail';
import { Integrations } from './pages/Integrations';
import { AppIntegrations } from './pages/h9/AppIntegrations';
import { H9Notices } from './pages/h9/Notices';
import { Login } from './pages/Login';
import { ChangePassword } from './pages/ChangePassword';
import { OrganizationDesign } from './pages/OrganizationDesign';
import { OrganizationBranding } from './components/OrganizationBranding';
import { OrgAdminList, OrgAdminDetail } from './pages/bo/OrgAdmin';
import { ScopeOrgProvider } from './nav/scopeOrg';
import { ActingBanner } from './components/ActingBanner';
import { OpsDashboard } from './pages/bo/OpsDashboard';
import { OpsSettings } from './pages/bo/OpsSettings';
import { ManagementHistory } from './pages/ManagementHistory';
import { HistoryNavigation, readScope } from './components/GroupActivityHistory';
import { Plans } from './pages/bo/Plans';
import { Notices } from './pages/bo/Notices';
import { DeviceGroups } from './pages/DeviceGroups';
import { GroupWorkspace } from './pages/GroupWorkspace';
import { toast, ToastHost } from './components/toast';
import { HoverTipLayer } from './components/hoverTip';
import { cn } from './utils/cn';
import { api } from './client/api';
import { API_BASE } from './client/config';
import { useServerConnection } from './client/ws';
import { NotificationsBell } from './nav/NotificationsBell';
import { ThemeToggle } from './nav/ThemeToggle';
import { PageTitle } from './nav/PageTitle';
import { PageHeader } from './nav/Breadcrumb';
import { PageContainer } from './nav/PageContainer';
import { useBoOperator } from './nav/useBoOperator';
import { useAdminRole } from './nav/useAdminRole';
import { TERM, describeWorkRole } from './labels/orgConcepts';
import { DevAccountSelect } from './components/DevAccountSwitcher';
import { useHnineGate } from './nav/useHnineGate';
import { useAuthGate } from './nav/useAuthGate';
import { refreshAuthMe, useAuthMe } from './nav/authMe';
import { ContentSkeleton } from './components/skeleton/ContentSkeleton';
import { AppSkeleton } from './components/skeleton/layouts/AppSkeleton';
import { Button } from './components/ui';

// =============================================================================
// App shell — HashRouter (file:// safe for packaged builds). v2.2 IA (W5):
// five top-level sections + device-scoped calibration. The persistent 48px
// status bar keeps NOW-playing + a global notifications bell visible.
//
// Top-level IA:
//   1. 미디어 관리   /media    (library, playlists, scene editor, integrations)
//   2. 스케줄 관리   /schedule (timeline + events + device targeting)
//   3. 디바이스 상세 /devices/:id (목록은 5로 통합, /devices는 리다이렉트)
//   4. 관리이력      /history
//   5. 구성 관리      /org      (현황·그룹·디바이스·조직 정보·관리자 — 전 역할, 탭은 역할별)
//   5b. H9 > 고객사 관리 /h9/tenants (운영사 전용, BO API)
// =============================================================================

// Auth gate (W11). Probes GET /api/auth/me on boot: a session (200) renders
// the app shell, no session / unreachable backend (401 / network error) renders
// the Login screen. AUTH-off local dev answers 200, so the app is never blocked.
export function App() {
  const { gate, recheck, serverError } = useAuthGate();

  // ToastHost는 게이트 상태와 무관하게 항상 마운트 — 로그인(guest) 화면에서도
  // 토스트가 보이도록 최상위에 둔다(ToastHost는 document.body로 포털).
  let content: JSX.Element;
  if (gate === 'checking') {
    content = serverError
      ? <AuthUnavailable message={serverError} onRetry={recheck} />
      : <AppSkeleton />;
  } else if (gate === 'guest') {
    content = <Login onAuthenticated={recheck} />;
  } else if (gate === 'password-change') {
    content = <ChangePassword onComplete={recheck} />;
  } else {
    content = <AppShell />;
  }

  return (
    <>
      {content}
      <ToastHost />
      {/* aria-description 말풍선(비활성 버튼의 '못 하는 이유' 등) — 기본 title은 긴 문구가 잘렸다(2026-10-01). */}
      <HoverTipLayer />
      <UpdateBanner />
    </>
  );
}

function AuthUnavailable({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-6 text-text">
      <div role="alert" className="w-full max-w-sm rounded border border-line bg-panel p-6 shadow-lg">
        <h1 className="text-lg font-semibold">서버에 연결할 수 없습니다</h1>
        <p className="mt-2 text-sm text-text-dim">{message}</p>
        <Button variant="primary" className="mt-5 w-full" onClick={onRetry}>다시 시도</Button>
      </div>
    </main>
  );
}

/**
 * 배포 감시 — 열려 있는 탭이 옛 번들로 계속 돌지 않도록, 서버의 최신 번들
 * 해시가 달라지면 새로고침을 권한다. 5분마다, 그리고 탭에 돌아올 때 확인한다.
 */
export function UpdateBanner() {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    const current = document.querySelector<HTMLScriptElement>('script[src*="assets/index-"]')
      ?.getAttribute('src')?.match(/index-[^"./]+\.js/)?.[0];
    if (!current) return; // 개발 서버 등 해시 번들이 아니면 감시하지 않는다.
    let active = true;
    const check = async () => {
      try {
        const html = await (await fetch(window.location.pathname, { cache: 'no-store' })).text();
        const latest = html.match(/index-[^"./]+\.js/)?.[0];
        if (active && latest && latest !== current) setStale(true);
      } catch { /* 오프라인 등은 조용히 넘어간다. */ }
    };
    const id = window.setInterval(() => void check(), 5 * 60_000);
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    return () => { active = false; window.clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);
  if (!stale) return null;
  return <div role="status" aria-label="새 버전 알림"
    className="fixed bottom-4 left-1/2 z-[70] flex w-max max-w-[calc(100vw-1.5rem)] -translate-x-1/2 items-center gap-3 rounded border border-line bg-panel px-4 py-2 shadow-lg max-md:bottom-[calc(4.5rem+env(safe-area-inset-bottom))]">
    <span className="text-sm">관리자 화면의 새 버전이 배포되었습니다.</span>
    <Button variant="primary" className="!py-1 text-sm" onClick={() => window.location.reload()}>새로고침</Button>
  </div>;
}

function AppShell() {
  // 소켓은 앱당 하나 — 페이지에서 부르면 중복 연결이 생긴다(client/ws.ts 주석).
  useServerConnection();
  return (
    // future 플래그 — v7 동작을 미리 켜 부팅 때마다 뜨던 경고 2건을 없앤다(검수 C15).
    <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/* 100vh 는 iOS 사파리에서 하단 도구 막대 뒤까지 포함해 탭바가 그 밑에 깔린다 — 보이는 영역(dvh) 기준. */}
      <div data-app-shell className="h-[100vh] supports-[height:100dvh]:h-[100dvh] flex flex-col overflow-hidden bg-bg text-text">
        <TopBar />
        {/* BO 운영자가 고객사를 대행해서 보는 중이면 항상 보이는 띠(2026-09-15 A안). */}
        <ActingBanner />
        <div className="flex-1 min-h-0 flex overflow-hidden">
          <SideNav />
          <main className="flex-1 min-w-0 min-h-0">
            <Routes>
              {/* 1. 미디어 관리 */}
              <Route path="/media/*" element={<MediaSection />} />
              {/* 통합송출 관리 (제안 기능) */}
              <Route path="/delivery" element={<Navigate to="/schedule" replace />} />
              {/* 콘텐츠 승인관리 (제안 기능) */}
              <Route path="/approval" element={<Navigate to="/schedule?section=review" replace />} />
              {/* 2. 스케줄 관리 */}
              <Route path="/schedule" element={<Schedules />} />
              {/* 디바이스 목록은 구성 관리 > 그룹·디바이스로 통합(2026-09-14). 옛 링크·북마크 보존. */}
              <Route path="/devices" element={<Navigate to="/org/groups" replace />} />
              <Route path="/devices/groups" element={<Navigate to="/org/groups" replace />} />
              <Route path="/devices/:id/*" element={<DeviceDetail />} />
              {/* 4. 관리 이력 (FO) — 조직·계정/감사 로그는 BO로 이동 */}
              <Route path="/history" element={<ManagementHistory />} />
              {/* 5. BRICK 어드민 (BO) — gated */}
              <Route path="/org/*" element={<OrgSection />} />
              {/* 레거시 BRICK 관리 경로 — 역할에 따라 조직 관리 / 고객사 관리로 보낸다. */}
              <Route path="/bo/*" element={<LegacyBoRedirect />} />
              {/* 조직 Owner용 디자인 설정은 삭제(2026-09-16 대교). 운영사 경로(/h9/tenants/:orgId/design)만 남는다. */}
              <Route path="/organization/design" element={<Navigate to="/org" replace />} />
              {/* 6. H9 — HNINE 사내 운영(연동·공지) — @hnine 게이트 */}
              <Route path="/h9/*" element={<H9Section />} />

              {/* ---- Legacy-path redirects (avoid broken links) ---- */}
              <Route path="/calibration" element={<Navigate to="/org/groups" replace />} />
              <Route path="/playlists" element={<Navigate to="/media" replace />} />
              <Route path="/schedules" element={<Navigate to="/schedule" replace />} />
              <Route path="/events" element={<Navigate to="/schedule" replace />} />
              <Route path="/device-groups" element={<Navigate to="/org/groups" replace />} />
              <Route path="/integrations" element={<Navigate to="/h9/integrations" replace />} />
              <Route path="/organization" element={<Navigate to="/org/admins" replace />} />
              <Route path="/audit-log" element={<Navigate to="/history" replace />} />
              <Route path="/admin/audit" element={<Navigate to="/history" replace />} />
              <Route path="/admin" element={<Navigate to="/history" replace />} />
              <Route path="/admin/*" element={<Navigate to="/history" replace />} />
              <Route path="/notifications" element={<Navigate to="/org/groups" replace />} />
              <Route path="/back-office" element={<Navigate to="/org" replace />} />

              {/* Default + wildcard → media */}
              <Route path="*" element={<Navigate to="/media" replace />} />
            </Routes>
          </main>
          <AssistantHost />
        </div>
        {/* 모바일 — 좌측 메뉴 대신 엄지가 닿는 하단 탭바(2026-09-23). */}
        <BottomNav />
        {/* 모바일 — 가로로 긴 표를 행마다 카드로(라벨 | 값). */}
        <MobileTableCards />
      </div>
    </HashRouter>
  );
}

// -----------------------------------------------------------------------------
// Section landings (group landing + sub-tabs). Existing page components are
// reused verbatim; only the route paths are rearranged under each section.
// -----------------------------------------------------------------------------

function MediaSection(): JSX.Element {
  // 외부 연동(Chat·Calendar)은 H9(HNINE 사내 운영) 섹션으로 이동(HNBL — H9 IA).
  // 미디어는 라이브러리·플레이리스트만 담당한다. 페이지 타이틀은 Playlists의
  // 좌·중앙 작업영역 안에 배치해 우측 인스펙터가 화면 헤더 바로 아래부터 시작한다.
  return (
    <PageContainer variant="full" className="flex flex-col overflow-hidden">
      <GroupWorkContext />
      <div className="min-h-0 flex-1 overflow-hidden">
        <Routes>
          <Route index element={<Playlists />} />
          {/* 연동은 H9로 이동 — 옛 링크 보존 */}
          <Route path="integrations" element={<Navigate to="/h9/integrations" replace />} />
          <Route path="*" element={<Navigate to="/media" replace />} />
        </Routes>
      </div>
    </PageContainer>
  );
}

// H9 — HNINE 사내 운영 섹션(공지사항 / 앱 연동 / 운영사). @hnine.com(로컬 dev 포함)만
// 진입. 좌측 네비 서브메뉴가 탭 역할을 하므로 상단 SubTabs는 두지 않는다.
function H9Section(): JSX.Element {
  const gate = useHnineGate();
  if (gate === 'checking') {
    return <PageContainer><ContentSkeleton label="H9 화면을 불러오는 중…" variant="cards" /></PageContainer>;
  }
  if (gate === 'denied') {
    return <Navigate to="/org/groups" replace />;
  }
  return (
    <div className="h-full min-h-0 overflow-hidden">
      <Routes>
        <Route index element={<Navigate to="/h9/notices" replace />} />
        {/* 공지사항 / 앱 연동 / 운영사 — 좌측 서브메뉴 세 항목으로 분리. 상단 탭 없음. */}
        <Route path="notices" element={<H9Notices />} />
        <Route path="integrations" element={<AppIntegrations />} />
        {/* 구 연동 상세 콘솔(폴링·재파싱 등 운영 도구) — 임시 보존 경로. */}
        <Route path="integrations/advanced" element={<Integrations />} />
        {/* 레거시 경로 보존 */}
        <Route path="notices/integrations" element={<Navigate to="/h9/integrations" replace />} />
        <Route path="notices/*" element={<Navigate to="/h9/notices" replace />} />
        {/* 고객사 관리 — 운영사가 고객 조직을 만들고·정지·삭제한다(BO API). */}
        <Route path="tenants/*" element={<TenantsSection />} />
        {/* 운영사 — 플랜·공지·설정 등 플랫폼 운영 도구. */}
        <Route path="ops/*" element={<H9OpsSection />} />
        <Route path="*" element={<Navigate to="/h9/notices" replace />} />
      </Routes>
    </div>
  );
}

// 그룹은 조직 관리(/org/groups)에서 다룬다. 디바이스 화면은 기기 목록·상세만.
// 구성 관리 — 고객 관점. 현황 / 그룹·디바이스(기기·배정·페어링·승인 방식·스코프) / 조직 정보·관리자.
// 2026-09-14 디바이스 메뉴를 흡수하며 전 역할에 열었다. 관리자(fo_admin)에게는 그룹·디바이스 탭만 남고,
// 그 안에서 서버가 담당 그룹 기기만 내려준다. 현황·조직 정보·관리자는 조직 Owner(fo_super)·H9(h9_super)만.
// 근거: docs/CONFIG_MANAGEMENT_MENU.md, docs/ORG_GROUP_CONCEPT_AUDIT.md §3-1.
function OrgSection(): JSX.Element {
  // 구성 관리 = 단일 화면(2026-09-15 대교 결정). 그룹·관리자·디바이스 워크스페이스 하나에 탭 없음.
  // 역할별 차이는 화면 안에서: 관리자 배정·추가는 조직 Owner만, 그룹 관리자는 담당 그룹만(서버가 깎아 준다).
  // 옛 탭 경로(groups·admins·setup)는 여기로 돌린다.
  return (
    // 모바일은 화면 전체가 한 번에 스크롤한다 — 안쪽 상자만 스크롤하면 제목·그룹 머리 위를 밀 때 아무것도 움직이지 않는다(2026-09-23).
    <div className="flex h-full flex-col overflow-hidden max-md:block max-md:overflow-y-auto">
      <div className="flex-none px-6 pt-6 max-md:px-4 max-md:pt-4">
        <PageTitle title={TERM.orgAdminMenu} />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden max-md:overflow-visible">
        <Routes>
          <Route index element={<GroupWorkspace />} />
          <Route path="*" element={<Navigate to="/org" replace />} />
        </Routes>
      </div>
    </div>
  );
}

// 레거시 /bo/* — 기존 링크·북마크를 새 위치로 보낸다.
function LegacyBoRedirect(): JSX.Element {
  const adminRole = useAdminRole();
  const location = useLocation();
  const rest = location.pathname.replace(/^\/bo\/?/, '');
  if (rest === 'audit') return <Navigate to="/history" replace />;
  if (rest === 'groups' || rest === 'institutions') return <Navigate to="/org/groups" replace />;
  if (rest === 'plans') return <Navigate to="/h9/ops/plans" replace />;
  if (rest === 'notices') return <Navigate to="/h9/ops/notices" replace />;
  if (['usage', 'platform', 'reports'].includes(rest)) return <Navigate to="/h9/ops" replace />;
  if (['impersonation', 'ai'].includes(rest)) return <Navigate to="/h9/ops/settings" replace />;
  if (rest.startsWith('org')) {
    const tail = rest.slice('org'.length); // '' | '/:orgId' | '/:orgId/design'
    if (tail) return <Navigate to={`/h9/tenants${tail}`} replace />;
    if (adminRole === 'checking') {
      return <PageContainer><ContentSkeleton label="화면을 불러오는 중…" variant="cards" /></PageContainer>;
    }
    return <Navigate to={adminRole === 'h9_super' ? '/h9/tenants' : '/org/admins'} replace />;
  }
  return <Navigate to="/org" replace />;
}

// H9 > 고객사 관리 — 운영사 관점. 데이터는 /api/bo/* 라 서버가 BO 운영자를 재검증한다.
function TenantsSection(): JSX.Element {
  const gate = useBoOperator();
  if (gate === 'checking') {
    return <PageContainer><ContentSkeleton label="고객사 화면을 불러오는 중…" variant="cards" /></PageContainer>;
  }
  if (gate === 'denied') {
    return <Navigate to="/h9/notices" replace />;
  }
  return (
    <div className="h-full min-h-0 overflow-hidden">
      <Routes>
        <Route index element={<OrgAdminList />} />
        <Route path=":orgId/*" element={<TenantDetailSection />} />
        <Route path="*" element={<Navigate to="/h9/tenants" replace />} />
      </Routes>
    </div>
  );
}

// 고객사 상세 = 그 고객의 조직 관리 화면을 운영사 스코프로 연다(같은 컴포넌트 재사용).
function TenantDetailSection(): JSX.Element {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const base = `/h9/tenants/${encodeURIComponent(orgId)}`;
  // 고객사 범위 — 상세 안의 디바이스 모달 등 모든 /api 호출이 ?orgId=<고객사> 를 붙인다(2026-10-06, 운영자 403).
  return (
    <ScopeOrgProvider orgId={orgId}>
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-none px-6 pt-6">
        <PageTitle className="mb-0" title="고객사 상세" tabs={[
          { to: base, label: '조직 정보 · 관리자', end: true },
          { to: `${base}/groups`, label: TERM.groupDevicesTab },
        ]} />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <Routes>
          <Route index element={<OrgAdminDetail />} />
          <Route path="groups" element={<DeviceGroups orgId={orgId} />} />
          <Route path="design" element={<SelectedOrganizationDesign />} />
          <Route path="*" element={<Navigate to={base} replace />} />
        </Routes>
      </div>
    </div>
    </ScopeOrgProvider>
  );
}

// H9 > 운영사 — 기존 BRICK 어드민의 운영 도구 탭 전부(조직 관리 제외).
// H9 게이트(에이치나인) 안쪽이지만 데이터는 /api/bo/* 라 서버가 BO 운영자를
// 재검증한다. FE에서도 비운영자는 밖으로 돌려보낸다.
function H9OpsSection(): JSX.Element {
  const gate = useBoOperator();
  if (gate === 'checking') {
    return <PageContainer><ContentSkeleton label="운영사 화면을 불러오는 중…" variant="cards" /></PageContainer>;
  }
  if (gate === 'denied') {
    return <Navigate to="/h9/notices" replace />;
  }
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-none px-6 pt-6">
        <PageTitle className="mb-0" title="운영사" tabs={[
          { to: '/h9/ops', label: '대시보드', end: true },
          { to: '/h9/ops/plans', label: '플랜' },
          { to: '/h9/ops/notices', label: '공지·배너' },
          { to: '/h9/ops/settings', label: '설정' },
        ]} />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <Routes>
          {/* 대시보드 — 모니터링·사용량·리포트를 한 화면으로 통합. */}
          <Route index element={<OpsDashboard />} />
          <Route path="plans" element={<Plans />} />
          <Route path="notices" element={<Notices />} />
          {/* 그룹은 조직 관리로 이동. */}
          <Route path="groups" element={<Navigate to="/org/groups" replace />} />
          {/* 설정 — 운영자·AI 모델·가장을 한 화면으로 통합. */}
          <Route path="settings" element={<OpsSettings />} />
          {/* 레거시 경로 → 통합 탭. 감사 로그는 관리 이력이 상위 호환. */}
          <Route path="operators" element={<Navigate to="/h9/ops/settings" replace />} />
          <Route path="impersonation" element={<Navigate to="/h9/ops/settings" replace />} />
          <Route path="ai" element={<Navigate to="/h9/ops/settings" replace />} />
          <Route path="audit" element={<Navigate to="/history" replace />} />
          <Route path="usage" element={<Navigate to="/h9/ops" replace />} />
          <Route path="platform" element={<Navigate to="/h9/ops" replace />} />
          <Route path="reports" element={<Navigate to="/h9/ops" replace />} />
          <Route path="*" element={<Navigate to="/h9/ops" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function TopBar() {
  const [me, setMe] = useState<AuthMe | null>(null);
  const [workRole, setWorkRole] = useState('권한 확인 중…');
  const { status: authStatus } = useAuthMe();

  useEffect(() => {
    let controller: AbortController | null = null;
    const load = () => {
      controller?.abort(); controller = new AbortController();
      const signal = controller.signal;
      setWorkRole('권한 확인 중…');
      // 관리 이력과 같은 요청이라 in-flight를 공유한다 — 따로 부르면 같은 틱에
      // /api/device-groups 가 두 번 나갔다(검수 S2).
      void readScope()
        .then(({ actor }) => { if (!signal.aborted) setWorkRole(describeWorkRole(actor)); })
        .catch(() => { if (!signal.aborted) setWorkRole('권한 확인 필요'); });
    };
    load(); window.addEventListener('bricklit:scope-changed', load); window.addEventListener('bricklit:auth-changed', load);
    return () => { controller?.abort(); window.removeEventListener('bricklit:scope-changed', load); window.removeEventListener('bricklit:auth-changed', load); };
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetch(`${API_BASE}/api/auth/me`, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: AuthMe | null) => setMe(data))
      .catch(() => {
        if (!ac.signal.aborted) setMe(null);
      });
    return () => ac.abort();
  }, []);

  const initial = (me?.name?.[0] ?? me?.email[0] ?? '장').toUpperCase();
  const roleLabel = me?.adminRole === 'h9_super' ? TERM.platformAdmin : workRole;
  const logout = () => {
    void fetch(`${API_BASE}/api/auth/logout`, { method: 'POST' })
      .then((r) => {
        if (!r.ok) throw new Error(`logout failed: ${r.status}`);
        window.location.reload();
      })
      .catch(() => {
        // 실패 알림은 앱 전역 토스트로 통일(2026-09-16 검수 S19).
        toast('로그아웃 실패 — 네트워크 확인 후 다시 시도하세요.', 'error');
      });
  };
  const avatar = me?.pictureUrl ? (
    <img src={me.pictureUrl} alt="" referrerPolicy="no-referrer" className="h-[26px] w-[26px] rounded-full" />
  ) : (
    <div className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-gradient-to-br from-accent to-accent-dim text-[12px] font-semibold text-[#1a0a04]">
      {initial}
    </div>
  );

  return (
    <header className="h-[58px] flex-none flex items-center gap-2 border-b border-line bg-panel px-3 md:gap-3 md:px-4">
      <div className="flex min-w-0 items-center gap-1.5 md:shrink-0">
        <OrganizationBranding size={20} className="block" />
      </div>

      <div className="ml-auto flex flex-none items-center gap-2 md:gap-3">
        {authStatus === 'checking' && (
          <span role="status" className="text-[13px] text-text-dim">권한을 확인하는 중…</span>
        )}
        {authStatus === 'unavailable' && (
          <div role="alert" className="flex items-center gap-2 text-[13px] text-danger">
            <span>권한 확인에 실패했습니다.</span>
            <button
              type="button"
              onClick={() => void refreshAuthMe()}
              className="underline underline-offset-2"
            >
              다시 시도
            </button>
          </div>
        )}
        {/* 개발용 계정 전환 — pnpm dev:roles 에서만 렌더(서버가 목록을 줄 때). */}
        <DevAccountSelect currentEmail={me?.email} />
        {/* Global notifications bell (W5 — replaces the left-nav 알림 센터). */}
        <NotificationsBell />
        {/* Light/Dark theme toggle (W8) — sits beside the bell. */}
        <div className="hidden md:block"><ThemeToggle /></div>
        <div className="hidden h-[22px] w-px bg-line md:block" />
        {/* 모바일은 아바타 하나만 두고 이름·권한·테마·로그아웃을 그 안에 접는다 — 상단바가 넘치지 않게. */}
        <MobileAccountMenu avatar={avatar} name={me?.name ?? me?.email ?? 'Operator'} email={me?.email ?? null}
          role={roleLabel} onLogout={me ? logout : null} />
        <div className="hidden items-center gap-2 md:flex" title={me?.email}>
          {avatar}
          <span className="max-w-[144px] truncate text-[14px] font-semibold text-text">
            {me?.name ?? me?.email ?? 'Operator'}
          </span>
          {/* 관리자 등급 표기(Figma GNB) — BO 운영자면 최고 관리자. */}
          <span className="flex items-center gap-2 text-[13px] text-text-dim">
            <span className="h-[14px] w-px bg-line" />
            {roleLabel}
          </span>
          {me && (
            <button
              type="button"
              onClick={logout}
              className="inline-flex h-7 items-center rounded-full border border-line px-2.5 text-[12px] text-text-dim hover:border-line-strong hover:text-text"
            >
              로그아웃
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

// 타임라인·기간 이벤트·기본 편성 모두 허용된 기기 목록에서 미리보기 가능함.
function SelectedOrganizationDesign() {
  const { orgId } = useParams<{ orgId: string }>();
  return <OrganizationDesign {...(orgId ? { orgId } : {})} />;
}

function SideNav() {
  const adminRole = useAdminRole();
  const linkCls = ({ isActive }: { isActive: boolean }) =>
    cn(
      'relative flex w-full items-center gap-2.5 rounded px-2 py-2 text-left transition-colors duration-fast md:px-2.5',
      isActive
        ? 'text-accent bg-accent-soft border border-accent-ring'
        : 'text-text-dim border border-transparent hover:text-text hover:bg-panel-hover',
    );
  return (
    <nav className="hidden w-[211px] flex-none flex-col gap-0.5 border-r border-line bg-panel p-2.5 md:flex">
      <NavLink to="/media" className={linkCls} title="플레이리스트 — 라이브러리·플레이리스트·Scene">
        {({ isActive }) => <NavItem active={isActive} icon={<MediaIcon />} label="플레이리스트" />}
      </NavLink>
      <NavLink to="/schedule" className={linkCls} title="스케줄 — 타임라인·이벤트·디바이스 타겟팅">
        {({ isActive }) => <NavItem active={isActive} icon={<ScheduleIcon />} label="스케줄" />}
      </NavLink>
      {/* 구성 관리 — 그룹·디바이스는 전 역할, 현황·조직 정보·관리자는 조직 Owner·H9만(OrgSection이 탭을 깎는다). 2026-09-14 디바이스 메뉴 통합.
          순서: 플레이리스트 > 스케줄 > 구성 관리 > 관리이력 (2026-09-14 대교). */}
      {adminRole !== 'checking' && (
        <NavLink to="/org" className={linkCls} title={`${TERM.orgAdminMenu} — 그룹·디바이스·관리자`}>
          {({ isActive }) => <NavItem active={isActive} icon={<SettingsIcon />} label={TERM.orgAdminMenu} />}
        </NavLink>
      )}
      <HistoryNavigation className={linkCls} title="관리 이력 — 조회 권한이 있는 운영·승인 이력">
        {({ isActive }) => <NavItem active={isActive} icon={<HistoryIcon />} label="관리 이력" />}
      </HistoryNavigation>
      {/* H9 — 최고 관리자(H9)만. */}
      {adminRole === 'h9_super' && <H9NavGroup linkCls={linkCls} />}
      <div className="flex-1" />
      <div className="flex flex-col gap-2 border-t border-line pt-2.5 pb-1">
        {/* 사용 매뉴얼 — /manual 은 로그인 없이 보는 공개 페이지라 새 탭으로 연다(2026-10-01 대교). */}
        <a href="/manual/" target="_blank" rel="noopener" title="관리자 사용 매뉴얼 — 새 탭에서 열립니다"
          className={linkCls({ isActive: false })}>
          <NavItem active={false} icon={<ManualIcon />} label="사용 매뉴얼" />
        </a>
        <span className="tk-tag px-1 md:px-2.5">v1.0</span>
      </div>
    </nav>
  );
}

/**
 * 모바일 하단 탭바 — 좌측 메뉴와 같은 목적지·같은 권한 조건. 아이콘 아래 짧은 이름을 붙여
 * 아이콘만으로 추측하지 않게 한다. 홈 표시줄(safe area) 위로 올린다.
 */
function BottomNav(): JSX.Element {
  const adminRole = useAdminRole();
  const location = useLocation();
  const [h9Open, setH9Open] = useState(false);
  const hiddenByScroll = useHideOnScrollDown();
  const navRef = useRef<HTMLElement | null>(null);
  const h9Ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    setHeight(el.offsetHeight);
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(() => setHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => { setH9Open(false); }, [location.pathname]);
  useEffect(() => {
    if (!h9Open) return;
    const onDown = (event: PointerEvent) => { if (!h9Ref.current?.contains(event.target as Node)) setH9Open(false); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [h9Open]);
  // H9 메뉴를 펼친 동안에는 숨기지 않는다.
  const collapsed = hiddenByScroll && !h9Open;
  const tabCls = ({ isActive }: { isActive: boolean }) => cn(
    'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-semibold transition-colors duration-fast',
    isActive ? 'text-accent' : 'text-text-dim',
  );
  const subCls = ({ isActive }: { isActive: boolean }) => cn('flex h-11 items-center rounded px-3 text-[15px]',
    isActive ? 'bg-accent-soft font-semibold text-accent' : 'text-text hover:bg-panel-2');
  return (
    // 본문을 아래로 내리면 밀어 숨기고, 위로 올리면 다시 올린다(2026-09-23 요청 — 방향 반전).
    // 음수 margin 으로 접어 본문이 그 자리를 쓰게 한다 — 떠 있는 막대가 마지막 줄을 가리지 않는다.
    <nav ref={navRef} aria-label="주 메뉴" data-collapsed={collapsed || undefined}
      style={{ marginBottom: collapsed ? -height : 0 }}
      className={cn('relative z-40 flex flex-none border-t border-line bg-panel pb-[env(safe-area-inset-bottom)] transition-[margin] duration-200 ease-out md:hidden',
        collapsed && 'pointer-events-none')}>
      <NavLink to="/media" className={tabCls}><MediaIcon /><span className="truncate">플레이리스트</span></NavLink>
      <NavLink to="/schedule" className={tabCls}><ScheduleIcon /><span className="truncate">스케줄</span></NavLink>
      {adminRole !== 'checking' && (
        <NavLink to="/org" className={tabCls}><SettingsIcon /><span className="truncate">{TERM.orgAdminMenu}</span></NavLink>
      )}
      <HistoryNavigation className={tabCls}>
        {() => <><HistoryIcon /><span className="truncate">관리 이력</span></>}
      </HistoryNavigation>
      {/* H9 은 하위 메뉴가 넷이라 한 목적지로 보낼 수 없다 — 누르면 위로 목록을 펼친다. */}
      {adminRole === 'h9_super' && <div ref={h9Ref} className="relative flex min-w-0 flex-1">
        <button type="button" aria-haspopup="menu" aria-expanded={h9Open} onClick={() => setH9Open((v) => !v)}
          className={tabCls({ isActive: location.pathname.startsWith('/h9') })}>
          <H9Icon /><span className="truncate">H9</span>
        </button>
        {h9Open && <div role="menu" aria-label="H9 메뉴"
          className="absolute bottom-full right-1 mb-2 flex w-48 flex-col gap-0.5 rounded-md border border-line bg-panel p-1.5 shadow-lg">
          <NavLink role="menuitem" to="/h9/tenants" className={subCls}>{TERM.tenantMenu}</NavLink>
          <NavLink role="menuitem" to="/h9/notices" className={subCls}>공지사항</NavLink>
          <NavLink role="menuitem" to="/h9/integrations" className={subCls}>앱 연동</NavLink>
          <NavLink role="menuitem" to="/h9/ops" className={subCls}>운영사</NavLink>
        </div>}
      </div>}
    </nav>
  );
}

/**
 * 본문을 아래로 내리면(scrollTop 증가) true, 위로 올리면 false. 이 앱은 창이 아니라 화면 안쪽
 * 상자가 스크롤되므로 문서 전체의 scroll 을 캡처 단계에서 받는다. 모달 안 스크롤과 가로 스크롤은
 * 무시하고, 관성 스크롤의 잔떨림에 흔들리지 않게 12px 이상 움직였을 때만 방향을 바꾼다.
 */
function useHideOnScrollDown(): boolean {
  const [hidden, setHidden] = useState(false);
  const location = useLocation();
  useEffect(() => { setHidden(false); }, [location.pathname]);
  useEffect(() => {
    const anchors = new WeakMap<Element, number>();
    const onScroll = (event: Event) => {
      const target = event.target === document ? document.scrollingElement : event.target;
      if (!(target instanceof Element) || target.closest('[data-modal-backdrop]')) return;
      // 처음 보는 상자는 맨 위(0)에서 출발한 것으로 본다 — 첫 스크롤부터 반응하게.
      const step = scrollStep(anchors.get(target) ?? 0, target.scrollTop, target.scrollHeight - target.clientHeight);
      anchors.set(target, step.anchor);
      if (step.hidden !== undefined) setHidden(step.hidden);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);
  return hidden;
}

/** 모바일 상단바의 프로필 버튼 — 이름·권한·테마·로그아웃을 한 곳에 접는다. */
function MobileAccountMenu({ avatar, name, email, role, onLogout }: {
  avatar: JSX.Element; name: string; email: string | null; role: string; onLogout: (() => void) | null;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => { if (!rootRef.current?.contains(event.target as Node)) setOpen(false); };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div ref={rootRef} className="relative md:hidden">
      <button type="button" aria-label="내 계정" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-full">
        {avatar}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-11 z-[80] w-64 rounded-md border border-line bg-panel p-3 shadow-lg">
          <p className="truncate text-[15px] font-semibold text-text">{name}</p>
          {email && email !== name && <p className="truncate text-[13px] text-text-dim">{email}</p>}
          <p className="mt-0.5 text-[13px] text-text-dim">{role}</p>
          <div className="my-3 h-px bg-line" />
          <div className="flex items-center justify-between">
            <span className="text-[14px] text-text">화면 테마</span>
            <ThemeToggle />
          </div>
          {onLogout && (
            <button type="button" role="menuitem" onClick={onLogout}
              className="mt-3 flex h-10 w-full items-center justify-center rounded border border-line text-[14px] text-text-dim hover:text-text">
              로그아웃
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// H9 접이식 그룹 — HNINE 사내 운영(연동·공지). /h9/* 경로면 기본 펼침.
function H9NavGroup({
  linkCls,
}: {
  linkCls: (s: { isActive: boolean }) => string;
}): JSX.Element {
  const location = useLocation();
  const onH9 = location.pathname.startsWith('/h9');
  const [open, setOpen] = useState(onH9);
  useEffect(() => {
    if (onH9) setOpen(true);
  }, [onH9]);

  const subCls = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center truncate rounded px-1.5 py-1.5 text-left text-[14px] transition-colors duration-fast md:px-2.5 md:pl-9',
      isActive
        ? 'text-accent bg-accent-soft border border-accent-ring'
        : 'text-text-dim border border-transparent hover:text-text hover:bg-panel-hover',
    );

  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="H9 — 운영사(에이치나인) 전용: 고객사·공지·연동·플랫폼 운영"
        className={cn(
          'relative flex w-full items-center gap-2.5 rounded px-2 py-2 text-left transition-colors duration-fast md:px-2.5',
          onH9
            ? 'text-accent'
            : 'text-text-dim hover:text-text hover:bg-panel-hover',
        )}
      >
        <span className="flex w-5 justify-center leading-none"><H9Icon /></span>
        <span className="hidden min-w-0 flex-1 flex-col leading-[1.15] md:flex">
          <span className="truncate text-[15px] font-semibold">H9</span>
        </span>
        <span className="hidden md:inline-flex"><ChevronIcon open={open} /></span>
      </button>
      {open && (
        <>
          <NavLink to="/h9/tenants" className={subCls}>{TERM.tenantMenu}</NavLink>
          <NavLink to="/h9/notices" className={subCls}>공지사항</NavLink>
          <NavLink to="/h9/integrations" className={subCls}>앱 연동</NavLink>
          <NavLink to="/h9/ops" className={subCls}>운영사</NavLink>
        </>
      )}
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={cn('shrink-0 transition-transform duration-fast', open ? 'rotate-180' : '')}
    >
      <path d="M6 8L10 12L14 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NavItem({ active, icon, label }: { active: boolean; icon: JSX.Element; label: string }) {
  return (
    <>
      {active && <span className="absolute -left-2.5 top-2 bottom-2 w-0.5 rounded bg-accent" />}
      <span className="flex w-5 justify-center leading-none">{icon}</span>
      <span className="hidden min-w-0 flex-col leading-[1.15] md:flex">
        <span className="truncate text-[15px] font-semibold">{label}</span>
      </span>
    </>
  );
}

function ManualIcon(): JSX.Element {
  return (
    <NavIcon>
      <path d="M3.5 4.2A1.2 1.2 0 0 1 4.7 3h4.1c.7 0 1.2.5 1.2 1.2V17l-1-1H4.7a1.2 1.2 0 0 1-1.2-1.2V4.2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M16.5 4.2A1.2 1.2 0 0 0 15.3 3h-4.1c-.7 0-1.2.5-1.2 1.2V17l1-1h4.3a1.2 1.2 0 0 0 1.2-1.2V4.2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </NavIcon>
  );
}

function NavIcon({ children }: { children: ReactNode }): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="shrink-0">
      {children}
    </svg>
  );
}

function MediaIcon(): JSX.Element {
  return (
    <NavIcon>
      <rect x="3" y="4.5" width="14" height="11" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8.3 7.7L12.7 10L8.3 12.3V7.7Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </NavIcon>
  );
}

function ScheduleIcon(): JSX.Element {
  return (
    <NavIcon>
      <rect x="3.5" y="4.5" width="13" height="12" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
      <path d="M6.5 3V6.2M13.5 3V6.2M4 8H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </NavIcon>
  );
}

/** 구성 관리 — 톱니(설정). Lucide `settings`(ISC) 경로를 20px 격자에 맞춰 축소. 손으로 그린 첫 버전은 별 모양처럼 보여 교체(2026-09-14). */
function SettingsIcon(): JSX.Element {
  return (
    <NavIcon>
      <g transform="translate(1 1) scale(0.75)" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </g>
    </NavIcon>
  );
}

/** 관리이력 — 기록 문서(줄 세 개). 2026-09-14 시계 모양에서 교체: 이력은 '시간'보다 '기록'이라서. */
function HistoryIcon(): JSX.Element {
  return (
    <NavIcon>
      <path d="M6 2.8h6l3.5 3.5V16.2a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.8a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 2.8v3.7h3.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M7.5 9.2h5M7.5 11.7h5M7.5 14.2h3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </NavIcon>
  );
}

function H9Icon(): JSX.Element {
  return (
    <NavIcon>
      <path d="M3.5 16.5V5.2c0-.5.4-.9.9-.9h6c.5 0 .9.4.9.9v11.3" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M11.3 16.5V8.6c0-.5.4-.9.9-.9h3.4c.5 0 .9.4.9.9v7.9" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M2.5 16.5h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M5.8 7h2.2M5.8 10h2.2M13.3 10.6h1.4M13.3 13.2h1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </NavIcon>
  );
}

function BackOfficeIcon(): JSX.Element {
  return (
    <NavIcon>
      <rect x="3.5" y="6" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M7.5 6V4.2c0-.6.4-1 1-1h3c.6 0 1 .4 1 1V6M3.5 10H16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </NavIcon>
  );
}
